import { useCallback, useEffect, useMemo, useState } from "react";
import { usePurchaseComponents } from "@contexts/PurchaseComponentsContext";

import { useContent } from "@api/contentful/content";
import type { BoostMultiplierSharedContent } from "@api/contentful/parsers/boostMultiplierShared";
import type { BoosterMultiplierOption } from "@api/products/boosterMultipliers";
import { useBoosterMultipliers } from "@api/products/boosterMultipliers";

import { CONTENT_KEY, FALLBACK_CURRENCY_CODE } from "../BalanceBoosterMultiplier.constants";
import { buildDisplayText, getFirstCardAvios, parseTermsContent } from "../BalanceBoosterMultiplier.helpers.tsx";
import type {
  BoostEligibilityChangeCallback,
  BoostErrorCallback,
  BoostRemoveCallback,
  BoostSelectCallback,
} from "../BalanceBoosterMultiplier.types";

/**
 * Props for the core booster hook
 */
export type UseBoosterCoreProps = {
  /** Base Avios amount for multiplier calculations */
  aviosAmount: number;
  /** Restores a previously selected boost on mount (the `id` from a prior `onBoostSelect`) */
  defaultSelectedBoostId?: string | null;
  /** Whether component is disabled */
  disabled?: boolean;
  /** Contentful content key override */
  contentKey?: typeof CONTENT_KEY;
  /** Callback handlers */
  onBoostSelect?: BoostSelectCallback;
  onBoostRemove?: BoostRemoveCallback;
  onError?: BoostErrorCallback;
  /** Fires with `(isEligible, disabledReason)` when the API's eligibility changes */
  onEligibilityChange?: BoostEligibilityChangeCallback;
};

/**
 * Return type from the core booster hook
 */
export type UseBoosterCoreReturn = {
  // State
  selectedBoostId: string | null;
  isLoading: boolean;

  // Data
  multiplierOptions: Array<BoosterMultiplierOption>;
  content: BoostMultiplierSharedContent | undefined;

  // Computed display values
  displayHeading?: string;
  displayBodyText: string;
  displayFinePrint?: string;
  displayRemoveBoostLabel?: string;
  displayTermsLabel?: string;
  displaySectionLabel?: string;
  displayBestValueLabel?: string;
  displayBoosterIcon?: string;
  displayTermsContent: React.ReactNode | null;

  // Handlers
  handleBoostSelect: (option: BoosterMultiplierOption) => void;
  handleRemoveBoost: () => void;
  handleResetBooster: () => void;

  // Config
  currency: string;
  locale: string;
  disabled: boolean;
  /** Why the booster is disabled by the API (e.g. NOT_AVAILABLE, NO_ALLOWANCE, BUSINESS_RULE_FAILURE), if at all */
  disabledReason: string | null;
};

/**
 * Core hook containing all business logic for the booster component
 * This hook is variant-agnostic and provides all necessary data and handlers
 */
export const useBoosterCore = ({
  aviosAmount,
  defaultSelectedBoostId = null,
  disabled = false,
  contentKey = CONTENT_KEY,
  onBoostSelect,
  onBoostRemove,
  onError,
  onEligibilityChange,
}: UseBoosterCoreProps): UseBoosterCoreReturn => {
  // Locale always comes from the provider (cookie-derived, always resolved)
  const { locale } = usePurchaseComponents();

  // State: selected boost card ID, seeded from defaultSelectedBoostId
  const [selectedBoostId, setSelectedBoostId] = useState<string | null>(defaultSelectedBoostId);

  // Fetch multiplier options from API
  const {
    data: multipliersData,
    isLoading,
    isError: isMultipliersError,
    error: multipliersError,
  } = useBoosterMultipliers(aviosAmount);
  const multiplierOptions = useMemo(() => multipliersData?.multipliers || [], [multipliersData?.multipliers]);

  // Currency always comes from the API, with a hard fallback for the pre-fetch state
  const resolvedCurrency = multipliersData?.currency ?? FALLBACK_CURRENCY_CODE;

  // Disabled if the caller says so, or the API says the customer isn't eligible to boost
  const resolvedDisabled = disabled || multipliersData?.isEligible === false;
  const disabledReason = multipliersData?.disabledReason ?? null;

  // Fetch content from Contentful
  const { data: content, isError: isContentError, error: contentError } = useContent(contentKey);

  // Notify the consumer when either the multiplier options or the content fails to load
  useEffect(() => {
    if (isMultipliersError) {
      onError?.(multipliersError);
    }
  }, [isMultipliersError, multipliersError, onError]);

  useEffect(() => {
    if (isContentError) {
      onError?.(contentError);
    }
  }, [isContentError, contentError, onError]);

  // Notify the consumer whenever the API's eligibility state changes
  useEffect(() => {
    if (multipliersData?.isEligible === undefined) return;

    onEligibilityChange?.(multipliersData.isEligible, disabledReason);
  }, [multipliersData?.isEligible, disabledReason, onEligibilityChange]);

  // Resolve defaultSelectedBoostId whenever the id or options change
  useEffect(() => {
    const matchedOption = multiplierOptions.find((option) => option.id === defaultSelectedBoostId);

    if (matchedOption) {
      setSelectedBoostId(matchedOption.id);
      onBoostSelect?.(matchedOption);
    } else {
      setSelectedBoostId(null);
    }
  }, [defaultSelectedBoostId, multiplierOptions, onBoostSelect]);

  // Computed values
  const firstCardAvios = useMemo(() => getFirstCardAvios(multiplierOptions), [multiplierOptions]);

  const displayBodyText = useMemo(
    () => buildDisplayText(content?.description, firstCardAvios, locale),
    [content?.description, firstCardAvios, locale],
  );

  const displayTermsContent = useMemo(
    () => (content?.termsList ? parseTermsContent(content.termsList) : null),
    [content?.termsList],
  );

  // Event handlers
  const handleBoostSelect = useCallback(
    (option: BoosterMultiplierOption) => {
      if (resolvedDisabled) return;

      setSelectedBoostId(option.id);
      onBoostSelect?.(option);

      if (import.meta.env.DEV) {
        console.log("Boost selected:", {
          id: option.id,
          percentage: option.percentage,
          aviosAmount: option.aviosAmount,
          price: option.price,
        });
      }
    },
    [resolvedDisabled, onBoostSelect],
  );

  const handleRemoveBoost = useCallback(() => {
    if (resolvedDisabled) return;

    setSelectedBoostId(null);
    onBoostRemove?.();

    if (import.meta.env.DEV) {
      console.log("Boost removed");
    }
  }, [resolvedDisabled, onBoostRemove]);

  const handleResetBooster = useCallback(() => {
    setSelectedBoostId(null);
  }, []);

  return {
    // State
    selectedBoostId,
    isLoading,

    // Data
    multiplierOptions,
    content,

    // Display values
    displayHeading: content?.heading,
    displayBodyText,
    displayFinePrint: content?.disclaimer,
    displayRemoveBoostLabel: content?.removeBoostButtonLabel,
    displayTermsLabel: content?.termsTitle,
    displaySectionLabel: content?.sectionLabel,
    displayBestValueLabel: content?.bestValue,
    displayBoosterIcon: content?.aviosBoostIcon?.url,
    displayTermsContent,

    // Handlers
    handleBoostSelect,
    handleRemoveBoost,
    handleResetBooster,

    // Config
    currency: resolvedCurrency,
    locale,
    disabled: resolvedDisabled,
    disabledReason,
  };
};
